import React from 'react';
import {
  Box,
  Paper,
  Grid,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  Assessment,
  CalendarToday,
  CompareArrows,
  Restaurant,
  AttachMoney,
  People,
  Inventory,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { glassStyle } from '../../theme/luxuryTheme';

interface QuickTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  category: string;
  filters: {
    comparisonMode: 'range' | 'trend' | 'comparison';
    datePreset?: string;
    metrics?: string[];
  };
}

const templates: QuickTemplate[] = [
  {
    id: 'daily-sales',
    name: 'Daily Sales Overview',
    description: 'Yesterday\'s performance across all stores',
    icon: <CalendarToday />,
    category: 'Sales',
    filters: {
      comparisonMode: 'range',
      datePreset: 'Yesterday',
      metrics: ['totalSales', 'orderCount', 'averageOrderValue'],
    },
  },
  {
    id: 'weekly-trend',
    name: 'Weekly Sales Trend',
    description: 'Last 7 days trend analysis',
    icon: <TrendingUp />,
    category: 'Sales',
    filters: {
      comparisonMode: 'trend',
      metrics: ['totalSales', 'orderCount'],
    },
  },
  {
    id: 'yoy-comparison',
    name: 'Year-Over-Year Comparison',
    description: 'Compare MTD vs last year MTD',
    icon: <CompareArrows />,
    category: 'Sales',
    filters: {
      comparisonMode: 'comparison',
      metrics: ['totalSales', 'orderCount', 'averageOrderValue'],
    },
  },
  {
    id: 'revenue-center',
    name: 'Revenue Center Analysis',
    description: 'Dine-in vs Takeout vs Delivery breakdown',
    icon: <Restaurant />,
    category: 'Operations',
    filters: {
      comparisonMode: 'range',
      datePreset: 'WTD',
      metrics: ['dineInSales', 'takeoutSales', 'deliverySales'],
    },
  },
  {
    id: 'store-comparison',
    name: 'Store Performance Comparison',
    description: 'Compare all stores side-by-side',
    icon: <Assessment />,
    category: 'Operations',
    filters: {
      comparisonMode: 'range',
      datePreset: 'MTD',
      metrics: ['totalSales', 'orderCount', 'averageOrderValue'],
    },
  },
  {
    id: 'financial-summary',
    name: 'Financial Summary',
    description: 'Net sales, tax, discounts breakdown',
    icon: <AttachMoney />,
    category: 'Financial',
    filters: {
      comparisonMode: 'range',
      datePreset: 'MTD',
      metrics: ['netSales', 'taxAmount', 'discounts'],
    },
  },
  {
    id: 'customer-insights',
    name: 'Customer Insights',
    description: 'Customer count and average spend',
    icon: <People />,
    category: 'Customer',
    filters: {
      comparisonMode: 'trend',
      metrics: ['orderCount', 'averageOrderValue'],
    },
  },
  {
    id: 'inventory-performance',
    name: 'Top Selling Items',
    description: 'Best performing products this month',
    icon: <Inventory />,
    category: 'Inventory',
    filters: {
      comparisonMode: 'range',
      datePreset: 'MTD',
      metrics: ['itemSales', 'itemQuantity'],
    },
  },
];

interface QuickReportTemplatesProps {
  onTemplateSelect: (template: QuickTemplate) => void;
}

const QuickReportTemplates: React.FC<QuickReportTemplatesProps> = ({ onTemplateSelect }) => {
  const categories = Array.from(new Set(templates.map((t) => t.category)));

  return (
    <Paper sx={{ ...glassStyle, p: 3, mb: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Quick Report Templates
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Select a pre-configured report template to get started quickly
        </Typography>
      </Box>

      {categories.map((category) => (
        <Box key={category} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
            {category}
          </Typography>
          <Grid container spacing={2}>
            {templates
              .filter((t) => t.category === category)
              .map((template, index) => (
                <Grid item xs={12} sm={6} md={3} key={template.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <Paper
                      sx={{
                        p: 2.5,
                        height: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        border: '1px solid rgba(255,255,255,0.1)',
                        background: 'linear-gradient(135deg, rgba(175, 35, 38, 0.05) 0%, rgba(0,0,0,0.2) 100%)',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(175, 35, 38, 0.3)',
                          border: '1px solid rgba(175, 35, 38, 0.5)',
                        },
                      }}
                      onClick={() => onTemplateSelect(template)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 2,
                            bgcolor: 'rgba(175, 35, 38, 0.2)',
                            color: 'primary.main',
                            mr: 1.5,
                          }}
                        >
                          {template.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {template.name}
                          </Typography>
                          <Chip
                            label={template.filters.comparisonMode.toUpperCase()}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              bgcolor: 'rgba(175, 35, 38, 0.2)',
                              color: 'primary.main',
                            }}
                          />
                        </Box>
                      </Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        {template.description}
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
          </Grid>
        </Box>
      ))}
    </Paper>
  );
};

export default QuickReportTemplates;
